<template>
  <div class="designer builder" dir="rtl">
    <div v-if="loading" class="b-state" role="status"><span class="spinner-border spinner-border-sm" aria-hidden="true" /> در حال بارگذاری فرم…</div>
    <div v-else-if="loadError" class="b-state"><EmptyState kind="error" title="فرم بارگذاری نشد" :description="loadError" /><button type="button" class="ins-btn" @click="load">تلاش دوباره</button></div>

    <template v-else-if="tree">
      <header class="b-bar">
        <router-link :to="listRoute" class="b-back" title="بازگشت به فهرست"><VnIcon name="arrow" /></router-link>
        <button type="button" class="b-name" @click="select({ kind: 'form' })">
          <small>{{ isVisit ? 'طراحی ویزیت' : 'طراحی پرسشنامه' }}</small>
          <b>{{ tree.form.verbose_name || 'بی‌نام' }}</b>
        </button>
        <span class="b-status" :class="statusTone" role="status">{{ statusText }}</span>
        <div class="b-actions">
          <button v-if="aiAvailable" type="button" class="ins-btn" @click="aiOpen = true"><VnIcon name="wand" />دستیار طراحی</button>
          <div class="b-seg" role="tablist" aria-label="نما">
            <button type="button" role="tab" :aria-selected="mode === 'design' ? 'true' : 'false'" :class="{ active: mode === 'design' }" @click="mode = 'design'"><VnIcon name="edit" />طراحی</button>
            <button type="button" role="tab" :aria-selected="mode === 'preview' ? 'true' : 'false'" :class="{ active: mode === 'preview' }" @click="mode = 'preview'"><VnIcon name="phone" />پیش‌نمایش اپ</button>
          </div>
          <button type="button" class="b-icon" title="واگرد (Ctrl+Z)" :disabled="!past.length && snapshot === current" @click="undo"><VnIcon name="undo" /></button>
          <button type="button" class="b-icon" title="ازنو (Ctrl+Shift+Z)" :disabled="!future.length" @click="redo"><VnIcon name="redo" /></button>
          <button type="button" class="b-problems" :class="{ bad: errors.length, warn: !errors.length && warnings.length }" :aria-expanded="problemsOpen ? 'true' : 'false'" @click="problemsOpen = !problemsOpen">
            <VnIcon name="alert" />{{ errors.length ? `${fa(errors.length)} ایراد` : warnings.length ? `${fa(warnings.length)} نکته` : 'بدون ایراد' }}
          </button>
          <button type="button" class="b-save" :disabled="saving || !dirty" @click="save">{{ saving ? 'در حال ذخیره…' : 'ذخیره' }}</button>
        </div>
        <span class="vn-bars b-bars" aria-hidden="true" />
      </header>

      <div v-if="conflict" class="b-banner danger" role="alert">
        <span>این فرم در این فاصله جای دیگری ذخیره شده است. برای جلوگیری از پاک شدن تغییرات دیگران، ذخیره متوقف شد.</span>
        <button type="button" class="ins-btn" @click="reloadFresh">بارگذاری نسخه‌ی تازه</button>
      </div>
      <div v-if="serverError" class="b-banner danger" role="alert"><span>{{ serverError }}</span><button type="button" class="ins-link" @click="serverError = ''">بستن</button></div>

      <div v-if="problemsOpen" class="b-problem-list">
        <p v-if="!problems.length" class="ins-note">ایرادی پیدا نشد.</p>
        <button v-for="(problem, index) in problems" :key="index" type="button" :class="problem.level" @click="goTo(problem.target)">
          <VnIcon name="alert" />{{ problem.message }}
        </button>
      </div>

      <nav v-if="isVisit" class="b-tracks" aria-label="مسیر">
        <button type="button" :class="{ active: track === 'visit' }" @click="setTrack('visit')">ویزیت کارشناس <span class="vn-num">{{ fa(countQuestions('visit')) }}</span></button>
        <button type="button" :class="{ active: track === 'supervision', off: !tree.form.has_supervision }" :title="tree.form.has_supervision ? '' : 'سرکشی برای این نوع ویزیت خاموش است'" @click="setTrack('supervision')">
          سرکشی سرپرست <span class="vn-num">{{ fa(countQuestions('supervision')) }}</span>
        </button>
        <label class="b-archived"><input v-model="showArchived" type="checkbox" />نمایش بایگانی‌شده‌ها <span v-if="archivedCount" class="vn-num">({{ fa(archivedCount) }})</span></label>
      </nav>
      <div v-else class="b-tracks"><label class="b-archived"><input v-model="showArchived" type="checkbox" />نمایش بایگانی‌شده‌ها <span v-if="archivedCount" class="vn-num">({{ fa(archivedCount) }})</span></label></div>

      <div v-if="isVisit && track === 'supervision' && !tree.form.has_supervision" class="b-banner">
        <span>سرکشی برای این نوع ویزیت خاموش است؛ مراحل این مسیر در اپ نمایش داده نمی‌شوند.</span>
        <button type="button" class="ins-btn" @click="tree.form.has_supervision = true">روشن کردن سرکشی</button>
      </div>

      <div class="b-grid" :class="{ previewing: mode === 'preview' }">
        <Palette v-show="mode === 'design'" class="b-palette" :types="catalog.answer_types" @add-question="addQuestion" @add-section="addSection" @add-photo="addPhoto()" />
        <main class="b-canvas">
          <Canvas v-if="mode === 'design'" :branch="branch" :selected-key="selectedKey" :types-by-id="typesById" :presets="presetsById"
                  :report-categories="reportCategories" :problems="problemsByKey" :show-archived="showArchived" :entry-photo="catalog.entry_photo"
                  :question-titles="questionTitles" @select="select" @add-question-to="section => addQuestion('YesNo', section)"
                  @duplicate-question="duplicateQuestion" @remove-question="removeQuestion" @remove-section="removeSection" @remove-photo="removePhoto" />
          <PhonePreview v-else :branch="branch" :form-name="tree.form.verbose_name" :types-by-id="typesById" :focus-key="selectedKey" />
        </main>
        <Inspector class="b-inspector" :target="target" :tree="tree" :track="track" :catalog="catalog" :types-by-id="typesById"
                   @select="select" @add-photo-for="addPhoto" />
      </div>
    </template>
    <div v-if="aiOpen" class="builder-ai-backdrop" @click.self="closeAI" @keydown.esc="closeAI">
      <section class="builder-ai-dialog" role="dialog" aria-modal="true" aria-labelledby="builder-ai-title">
        <header><h2 id="builder-ai-title">افزودن مراحل با دستیار</h2><button ref="aiClose" type="button" class="ins-btn" aria-label="بستن دستیار" :disabled="aiBusy" @click="closeAI"><VnIcon name="x" /></button></header>
        <div class="builder-ai-body"><AIFormAssistant :kind="isVisit ? 'visit' : 'survey'" :category="tree.form.outlet_category" :answer-types="catalog.answer_types" :initial-draft="aiDraft" @draft="aiDraft = $event" @busy="aiBusy = $event" /></div>
        <footer><p>مراحل پیشنهادی به فرم اضافه می‌شوند. برای ثبت نهایی، دکمه ذخیره فرم را بزنید.</p><button type="button" class="b-save" :disabled="aiBusy || !aiDraft" @click="appendAI">افزودن به فرم</button></footer>
      </section>
    </div>
  </div>
</template>

<script>
import VnIcon from '@/components/VnIcon/index.vue';
import EmptyState from '@/components/EmptyState/index.vue';
import Palette from './components/Palette.vue';
import Canvas from './components/Canvas.vue';
import Inspector from './components/Inspector.vue';
import PhonePreview from './components/PhonePreview.vue';
import AIFormAssistant from './components/AIFormAssistant.vue';
import { cloneQuestion, findProblems, newPhoto, newQuestion, newSection, prepare, questionRef, targetFromPath, fa } from './designerModel';
import './designer.css';

const HISTORY = 60;
// what is sent and compared: the tree without client keys
const serialize = tree => JSON.stringify(tree, (key, value) => (key === '_k' ? undefined : value));

export default {
  name: 'FormBuilder',
  components: { VnIcon, EmptyState, Palette, Canvas, Inspector, PhonePreview, AIFormAssistant },
  beforeRouteLeave(to, from, next) { next(!this.dirty || window.confirm('تغییرات ذخیره‌نشده از دست می‌رود. خارج می‌شوید؟')); },
  beforeRouteUpdate(to, from, next) { next(!this.dirty || window.confirm('تغییرات ذخیره‌نشده از دست می‌رود. ادامه می‌دهید؟')); },
  data: () => ({
    tree: null, catalog: null, loading: true, loadError: '', saving: false, saved: '', conflict: false, serverError: '',
    track: 'visit', mode: 'design', target: { kind: 'form' }, showArchived: false, problemsOpen: false,
    past: [], future: [], snapshot: '', restoring: false, savedAt: '',
    aiAvailable: false, aiOpen: false, aiBusy: false, aiDraft: null,
  }),
  computed: {
    isVisit() { return this.$route.meta.kind === 'visit'; },
    project() { return this.$STORE.state.userConfig.setProjectId; },
    base() { return `/core/api/admin/designer/${this.isVisit ? 'visit_types' : 'surveys'}/${this.$route.params.id}/?p=${this.project}`; },
    listRoute() { return this.isVisit ? '/designer/visits' : '/designer/surveys'; },
    branch() { return this.tree.tracks[this.track] || this.tree.tracks.visit; },
    typesById() { return Object.fromEntries((this.catalog ? this.catalog.answer_types : []).map(type => [type.id, type])); },
    presetsById() { return Object.fromEntries((this.catalog ? this.catalog.validations : []).map(rule => [rule.id, rule])); },
    reportCategories() { return this.isVisit ? this.catalog.report_categories : this.catalog.survey_report_categories; },
    current() { return this.tree ? serialize(this.tree) : ''; },
    dirty() { return !!this.tree && this.current !== this.saved; },
    problems() { return this.tree ? findProblems(this.tree, this.typesById) : []; },
    errors() { return this.problems.filter(problem => problem.level === 'error'); },
    warnings() { return this.problems.filter(problem => problem.level === 'warning'); },
    // the first problem of each row, shown on its card
    problemsByKey() {
      return this.problems.reduce((map, problem) => {
        const key = problem.target.item && problem.target.item._k;
        if (key && (!map[key] || (map[key].level === 'warning' && problem.level === 'error'))) map[key] = problem;
        return map;
      }, {});
    },
    selectedKey() { return (this.target.item && this.target.item._k) || ''; },
    questionTitles() {
      const titles = {};
      Object.values(this.tree.tracks).forEach(branch => branch.sections.forEach(section => section.questions.forEach((question) => { titles[questionRef(question)] = question.text || 'بی‌متن'; })));
      return titles;
    },
    archivedCount() {
      const branch = this.branch;
      return branch.sections.filter(s => !s.is_active).length + branch.photos.filter(p => !p.is_active).length
        + branch.sections.reduce((sum, s) => sum + s.questions.filter(q => !q.is_active).length, 0);
    },
    statusText() {
      if (this.saving) return 'در حال ذخیره…';
      if (this.dirty) return 'تغییرات ذخیره نشده';
      return this.savedAt ? `ذخیره شد · ${this.savedAt}` : 'همه‌چیز ذخیره است';
    },
    statusTone() { return this.dirty ? 'dirty' : 'clean'; },
  },
  watch: {
    aiOpen(value) { if (value) this.$nextTick(() => this.$refs.aiClose && this.$refs.aiClose.focus()); },
    // one undo step per pause in editing
    current(value) {
      if (this.restoring || !value) return;
      clearTimeout(this.historyTimer);
      this.historyTimer = setTimeout(() => {
        if (this.snapshot && this.snapshot !== value) {
          this.past.push(this.snapshot);
          if (this.past.length > HISTORY) this.past.shift();
          this.future = [];
        }
        this.snapshot = value;
      }, 400);
    },
    '$route.params.id'() { this.load(); },
  },
  mounted() {
    this.load();
    this.onKey = event => this.shortcut(event);
    this.onUnload = (event) => { if (this.dirty) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('beforeunload', this.onUnload);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('beforeunload', this.onUnload);
    clearTimeout(this.historyTimer);
  },
  methods: {
    fa,
    async load() {
      this.loading = true; this.loadError = ''; this.conflict = false; this.serverError = '';
      const [catalog, tree, ai] = await Promise.all([
        this.$ApiServiceLayer.get(`/core/api/admin/designer/catalog/?p=${this.project}`),
        this.$ApiServiceLayer.get(this.base),
        this.$ApiServiceLayer.get(`/core/api/ai/capabilities/?p=${this.project}`),
      ]);
      this.loading = false;
      if (catalog.status !== 200 || tree.status !== 200) {
        this.loadError = this.$ApiServiceLayer.getErrorMessage(catalog.status !== 200 ? catalog : tree);
        return;
      }
      this.catalog = catalog.data;
      this.aiAvailable = ai.status === 200 && ai.data.enabled;
      this.adopt(tree.data);
      this.past = []; this.future = [];
      this.target = { kind: 'form' };
    },
    adopt(data) {
      this.restoring = true;
      this.tree = prepare(data);
      this.saved = serialize(this.tree);
      this.snapshot = this.saved;
      this.$nextTick(() => { this.restoring = false; });
    },
    closeAI() { if (!this.aiBusy) this.aiOpen = false; },
    appendAI() {
      if (!this.aiDraft || this.aiBusy) return;
      const addition = JSON.parse(JSON.stringify(this.aiDraft.tree));
      const prefix = 'ai-' + Date.now().toString(36) + '-';
      Object.values(addition.tracks).forEach(branch => branch.sections.forEach(section => section.questions.forEach(question => { question.key = prefix + question.key; })));
      prepare(addition);
      Object.entries(addition.tracks).forEach(([track, branch]) => {
        if (!this.tree.tracks[track]) this.$set(this.tree.tracks, track, { sections: [], photos: [] });
        this.tree.tracks[track].sections.push(...branch.sections);
        this.tree.tracks[track].photos.push(...branch.photos);
      });
      if (addition.form.has_supervision) this.tree.form.has_supervision = true;
      this.aiOpen = false; this.aiDraft = null; this.mode = 'design';
      this.$notify({ group: 'tc', type: 'success', text: 'مراحل پیشنهادی اضافه شدند؛ فرم را بررسی و ذخیره کنید.' });
    },
    async reloadFresh() { this.saved = this.current; await this.load(); },
    setTrack(track) { this.track = track; this.target = { kind: 'form' }; },
    countQuestions(track) { return (this.tree.tracks[track] || { sections: [] }).sections.reduce((sum, s) => sum + s.questions.filter(q => q.is_active).length, 0); },
    select(target) { this.target = target && target.item !== undefined ? target : { kind: target ? target.kind : 'form' }; },
    goTo(target) {
      if (target.track && target.track !== this.track) this.track = target.track;
      this.mode = 'design';
      this.select(target);
      this.$nextTick(() => { const el = this.$el.querySelector('.selected'); if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' }); });
    },

    // ── editing ──
    targetSection() {
      if (this.target.kind === 'section') return this.target.item;
      if (this.target.kind === 'question') return this.target.section;
      return this.branch.sections.filter(section => section.is_active).slice(-1)[0] || null;
    },
    addQuestion(kindName, into) {
      let section = into || this.targetSection();
      if (!section) { section = newSection(fa(this.branch.sections.length + 1)); section.verbose_name = 'سوالات عمومی'; this.branch.sections.push(section); }
      const question = newQuestion(kindName, this.catalog.answer_types);
      const index = this.target.kind === 'question' && this.target.section === section ? section.questions.indexOf(this.target.item) + 1 : section.questions.length;
      section.questions.splice(index, 0, question);
      this.goTo({ kind: 'question', track: this.track, item: question, section });
    },
    addSection() {
      const section = newSection(fa(this.branch.sections.length + 1));
      this.branch.sections.push(section);
      this.goTo({ kind: 'section', track: this.track, item: section });
    },
    addPhoto(question) {
      const photo = newPhoto(fa(this.branch.photos.length + 1));
      if (question && question.text !== undefined) {
        photo.question = questionRef(question);
        photo.verbose_name = `تصویر: ${(question.text || 'پرسش').slice(0, 30)}`;
        photo.is_mandatory = false; photo.min = 0;
      }
      this.branch.photos.push(photo);
      this.goTo({ kind: 'photo', track: this.track, item: photo });
    },
    duplicateQuestion({ section, index }) {
      const copy = cloneQuestion(section.questions[index]);
      section.questions.splice(index + 1, 0, copy);
      this.goTo({ kind: 'question', track: this.track, item: copy, section });
    },
    // rows with field data are archived (and kept); new or empty rows are removed
    removeQuestion({ section, question }) {
      if (question.id && question.answered) question.is_active = false;
      else {
        section.questions.splice(section.questions.indexOf(question), 1);
        this.unlinkPhotos([questionRef(question)]);
      }
      if (this.target.item === question) this.select({ kind: 'section', track: this.track, item: section });
    },
    removeSection(section) {
      const answered = section.questions.some(question => question.id && question.answered);
      if (answered) {
        section.is_active = false;
        section.questions.forEach((question) => { question.is_active = false; });
      } else {
        if (section.questions.length && !window.confirm(`بخش «${section.verbose_name}» با ${fa(section.questions.length)} پرسش حذف شود؟`)) return;
        this.branch.sections.splice(this.branch.sections.indexOf(section), 1);
        this.unlinkPhotos(section.questions.map(questionRef));
      }
      this.select({ kind: 'form' });
    },
    removePhoto(photo) {
      if (photo.id && photo.photos) photo.is_active = false;
      else this.branch.photos.splice(this.branch.photos.indexOf(photo), 1);
      if (this.target.item === photo) this.select({ kind: 'form' });
    },
    unlinkPhotos(refs) {
      Object.values(this.tree.tracks).forEach(branch => branch.photos.forEach((photo) => { if (refs.includes(photo.question)) photo.question = null; }));
    },

    // ── history ──
    restore(value) {
      this.restoring = true;
      const key = this.selectedKey;
      this.tree = prepare(JSON.parse(value));
      this.snapshot = value;
      this.reselect(key);
      this.$nextTick(() => { this.restoring = false; });
    },
    undo() {
      clearTimeout(this.historyTimer);
      if (this.snapshot && this.snapshot !== this.current) { this.past.push(this.snapshot); this.snapshot = this.current; } // an edit still waiting for its pause
      if (!this.past.length) return;
      this.future.push(this.current);
      this.restore(this.past.pop());
    },
    redo() {
      if (!this.future.length) return;
      this.past.push(this.current);
      this.restore(this.future.pop());
    },
    reselect(key) {
      if (!key) { this.target = { kind: 'form' }; return; }
      for (const [track, branch] of Object.entries(this.tree.tracks)) {
        for (const section of branch.sections) {
          if (section._k === key) { this.target = { kind: 'section', track, item: section }; return; }
          const question = section.questions.find(row => row._k === key);
          if (question) { this.target = { kind: 'question', track, item: question, section }; return; }
        }
        const photo = branch.photos.find(row => row._k === key);
        if (photo) { this.target = { kind: 'photo', track, item: photo }; return; }
      }
      this.target = { kind: 'form' };
    },
    shortcut(event) {
      const mod = event.ctrlKey || event.metaKey;
      if (!mod) return;
      const key = event.key.toLowerCase();
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement && document.activeElement.tagName);
      if (key === 's') { event.preventDefault(); this.save(); } else if (!typing && key === 'z' && !event.shiftKey) { event.preventDefault(); this.undo(); } else if (!typing && (key === 'y' || (key === 'z' && event.shiftKey))) { event.preventDefault(); this.redo(); }
    },

    // ── save ──
    async save() {
      if (this.saving || !this.dirty) return;
      if (this.errors.length) {
        this.problemsOpen = true;
        this.goTo(this.errors[0].target);
        this.$notify({ group: 'tc', type: 'error', text: `پیش از ذخیره، ${fa(this.errors.length)} ایراد را برطرف کنید.` });
        return;
      }
      this.saving = true; this.serverError = '';
      const sent = this.current;
      const res = await this.$ApiServiceLayer.put(this.base, '', JSON.parse(sent));
      this.saving = false;
      if (res.status === 200) {
        const place = this.locate();
        this.adopt(res.data);
        this.pick(place);
        this.conflict = false;
        this.savedAt = new Date().toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
        this.$notify({ group: 'tc', type: 'success', text: 'فرم ذخیره شد؛ اپ میدانی از همین حالا نسخه‌ی تازه را نشان می‌دهد.' });
        return;
      }
      if (res.status === 409) { this.conflict = true; return; }
      const detail = res.data && (res.data.detail || (res.data.revision && res.data.revision[0]));
      const path = res.data && res.data.path;
      this.serverError = detail ? String(detail) : this.$ApiServiceLayer.getErrorMessage(res);
      const target = targetFromPath(this.tree, Array.isArray(path) ? path[0] : path);
      if (target && (target.item || target.kind === 'form')) this.goTo(target);
    },
    // new rows get ids (and new keys) on save, so the selection is carried over by position
    locate() {
      const { kind, item } = this.target;
      if (!item) return { kind: 'form' };
      for (const [track, branch] of Object.entries(this.tree.tracks)) {
        const p = branch.photos.indexOf(item);
        if (p >= 0) return { kind, track, p };
        for (let s = 0; s < branch.sections.length; s += 1) {
          if (branch.sections[s] === item) return { kind, track, s };
          const q = branch.sections[s].questions.indexOf(item);
          if (q >= 0) return { kind, track, s, q };
        }
      }
      return { kind: 'form' };
    },
    pick(place) {
      const branch = place.track && this.tree.tracks[place.track];
      if (!branch) { this.target = { kind: 'form' }; return; }
      if (place.kind === 'photo' && branch.photos[place.p]) { this.target = { kind: 'photo', track: place.track, item: branch.photos[place.p] }; return; }
      const section = branch.sections[place.s];
      if (!section) { this.target = { kind: 'form' }; return; }
      const question = place.kind === 'question' && section.questions[place.q];
      this.target = question ? { kind: 'question', track: place.track, item: question, section } : { kind: 'section', track: place.track, item: section };
    },
  },
};
</script>

<style scoped>
.builder-ai-backdrop { position:fixed; inset:0; z-index:1060; padding:16px; display:grid; place-items:center; background:rgba(2,11,45,.55); }
.builder-ai-dialog { display:flex; flex-direction:column; width:min(720px,100%); max-height:calc(100vh - 32px); border-radius:var(--r-md); background:var(--surface); overflow:hidden; box-shadow:var(--shadow-pop); }
.builder-ai-dialog > header { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:14px 18px; border-bottom:1px solid var(--line); }.builder-ai-dialog h2 { margin:0; color:var(--navy-900); font-size:17px; font-weight:900; }
.builder-ai-body { overflow:auto; padding:18px; }.builder-ai-dialog footer { display:flex; align-items:center; gap:12px; padding:14px 18px; border-top:1px solid var(--line); background:var(--surface-sunken); }.builder-ai-dialog footer p { flex:1; margin:0; color:var(--text-3); font-size:12px; line-height:1.9; }
.builder { display: grid; gap: 12px; }
.b-state { display: grid; justify-items: center; gap: 12px; padding: 60px 20px; color: var(--text-3); font-size: 13px; }
.b-bar { position: sticky; top: 0; z-index: 6; display: flex; align-items: center; flex-wrap: wrap; gap: 10px 14px; padding: 10px 12px 13px; border-radius: var(--r-md); background: var(--navy-900); color: #fff; overflow: hidden; }
.b-bars { position: absolute; inset-inline: 0; bottom: 0; direction: ltr; }
.b-back { width: 36px; height: 36px; display: grid; place-items: center; border-radius: var(--r-xs); border: 1px solid var(--shell-line); color: #fff; transform: scaleX(-1); }
.b-back:hover { background: var(--navy-800); color: #fff; }
.b-name { display: grid; min-width: 0; padding: 0; border: 0; background: transparent; color: #fff; text-align: right; font: inherit; cursor: pointer; }
.b-name small { font-size: 11px; color: var(--shell-text-2); }
.b-name b { font-size: 15px; font-weight: 900; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 36vw; }
.b-status { padding: 3px 10px; border-radius: 99px; font-size: 11px; font-weight: 800; }
.b-status.clean { background: rgba(18, 184, 192, .16); color: var(--teal-300); }
.b-status.dirty { background: rgba(255, 178, 15, .18); color: var(--amber-300); }
.b-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-inline-start: auto; }
.b-seg { display: inline-flex; gap: 2px; padding: 3px; border-radius: var(--r-sm); background: var(--navy-850); border: 1px solid var(--shell-line); }
.b-seg button { display: inline-flex; align-items: center; gap: 6px; min-height: 32px; padding: 0 12px; border: 0; border-radius: var(--r-xs); background: transparent; color: var(--shell-text-2); font: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
.b-seg button .vn-icon { width: 15px; height: 15px; }
.b-seg button.active { background: var(--teal-500); color: var(--navy-900); --icon-accent: var(--navy-900); }
.b-icon { width: 36px; height: 36px; display: grid; place-items: center; border: 1px solid var(--shell-line); border-radius: var(--r-xs); background: transparent; color: #fff; cursor: pointer; }
.b-icon:disabled { opacity: .35; cursor: default; }
.b-icon:hover:not(:disabled) { background: var(--navy-800); }
.b-problems { display: inline-flex; align-items: center; gap: 6px; min-height: 36px; padding: 0 12px; border: 1px solid var(--shell-line); border-radius: var(--r-xs); background: transparent; color: var(--shell-text-2); font: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
.b-problems .vn-icon { width: 15px; height: 15px; }
.b-problems.bad { background: var(--pink-500); border-color: var(--pink-500); color: #fff; }
.b-problems.warn { color: var(--amber-300); border-color: rgba(255, 178, 15, .4); }
.b-save { min-height: 38px; padding: 0 20px; border: 2px solid var(--teal-500); border-radius: var(--r-xs); background: var(--teal-500); color: var(--navy-900); font: inherit; font-size: 13px; font-weight: 900; cursor: pointer; box-shadow: 3px 3px 0 var(--pink-500); }
.b-save:disabled { background: transparent; color: var(--shell-text-3); border-color: var(--shell-line); box-shadow: none; cursor: default; }
.b-banner { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; padding: 10px 14px; border-radius: var(--r-sm); background: var(--amber-50); color: var(--amber-700); font-size: 12.5px; font-weight: 700; }
.b-banner.danger { background: var(--pink-50); color: var(--pink-700); }
.b-problem-list { display: grid; gap: 4px; padding: 10px; border: 1px solid var(--line); border-radius: var(--r-sm); background: var(--surface); max-height: 220px; overflow-y: auto; }
.b-problem-list button { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border: 0; border-radius: var(--r-xs); background: transparent; font: inherit; font-size: 12px; font-weight: 700; text-align: right; cursor: pointer; }
.b-problem-list button .vn-icon { flex: none; width: 15px; height: 15px; }
.b-problem-list button.error { color: var(--pink-700); }
.b-problem-list button.warning { color: var(--amber-700); }
.b-problem-list button:hover { background: var(--navy-50); }
.b-tracks { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
.b-tracks > button { display: inline-flex; align-items: center; gap: 8px; min-height: 36px; padding: 0 14px; border: 1px solid var(--line); border-radius: var(--r-xs); background: var(--surface); font: inherit; font-size: 12.5px; font-weight: 800; color: var(--text-2); cursor: pointer; }
.b-tracks > button span { padding: 0 6px; border-radius: 99px; background: var(--navy-100); font-size: 11px; }
.b-tracks > button.active { background: var(--navy-900); border-color: var(--navy-900); color: #fff; box-shadow: 3px 3px 0 var(--teal-500); }
.b-tracks > button.active span { background: var(--teal-500); color: var(--navy-900); }
.b-tracks > button.off:not(.active) { opacity: .6; }
#app .b-archived { display: inline-flex; align-items: center; gap: 6px; margin: 0 auto 0 0; font-size: 12px; font-weight: 700; color: var(--text-3); cursor: pointer; }
.b-archived input { accent-color: var(--navy-900); }
.b-grid { display: grid; grid-template-columns: 236px minmax(0, 1fr) 380px; gap: 16px; align-items: start; }
.b-grid.previewing { grid-template-columns: minmax(0, 1fr) 380px; }
.b-palette { position: sticky; top: 76px; max-height: calc(100vh - 100px); overflow-y: auto; padding-bottom: 12px; }
.b-canvas { min-width: 0; padding-bottom: 80px; }
.b-inspector { position: sticky; top: 76px; max-height: calc(100vh - 100px); }
@media (max-width: 1280px) { .b-grid { grid-template-columns: 200px minmax(0, 1fr) 330px; } }
@media (max-width: 1080px) {
  .b-grid, .b-grid.previewing { grid-template-columns: minmax(0, 1fr); }
  .b-palette { position: static; max-height: none; }
  .b-palette ::v-deep .pal-list { grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); }
  .b-inspector { position: static; max-height: none; }
  .b-name b { max-width: 60vw; }
}
</style>
